import { DragDropProvider } from "@dnd-kit/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import {
  Box,
  Button,
  Stack,
  Text,
  ActionIcon,
  Flex,
  Select,
  Tooltip,
} from "@mantine/core";
import { useParams } from "next/navigation";
import { useEffect, useMemo, useCallback, type ReactNode } from "react";

import { ChoiceOption } from "./choice-option";
import { AddConditionButton } from "../add-condition-button";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useGetDragEndEventHandler } from "@/hooks/dnd-kit.hooks";
import {
  useChoiceRulesWithDuplicates,
  type ChoiceRuleWithId,
} from "@/hooks/use-choice-rules-with-duplicates";
import {
  type ChoiceBasicQuestionQuestionType,
  ChoiceQualifyingExpression,
  QuestionStateSetter,
  QuestionType,
  type ReviewPageDraftQuestion,
} from "@/types/screener-builder";
import type { Maybe } from "@/types/utils";
import { isChoiceQuestion } from "@/utils/questions";
import type { Condition } from "@/types/screener-builder";

export const choiceRulesValid = (
  question: ReviewPageDraftQuestion<ChoiceBasicQuestionQuestionType>,
): boolean => {
  const rules = question.validationRules?.choiceRules ?? [];

  if (rules.length < 2) {
    return false;
  }

  const lowerCaseTexts = rules.map((r) => r.optionText.trim().toLowerCase());
  return lowerCaseTexts.length === new Set(lowerCaseTexts).size;
};

const MAX_CHOICES = 12;

// Helper to strip temp IDs when syncing rules back to question
const stripTempIds = (rules: ChoiceRuleWithId[]): Condition[] => {
  return rules.map(({ optionText, qualifier }) => ({
    optionText,
    qualifier,
  }));
};

interface UseChoiceQuestionComponentsReturn {
  renderOptions: () => ReactNode;
  renderValidationRules: () => ReactNode;
}

// We use a hook rather than a component here because both the options and the
// qualifying conditions need to be aware of qualifying state, but they live in
// structurally separate portions of the UI
export const useChoiceQuestionComponents = (
  question: ReviewPageDraftQuestion,
  setQuestion: QuestionStateSetter<ChoiceBasicQuestionQuestionType>,
): UseChoiceQuestionComponentsReturn => {
  const isChoice = isChoiceQuestion(question);

  const { id: screenerId } = useParams<{ id?: string }>() ?? {};
  const track = useTrackAnalytics({ screener_id: screenerId });

  const initialRules = useMemo(
    () => (isChoice && question.validationRules?.choiceRules) || [],
    // Only intended to fire on first render for initial rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    choiceRulesWithIds,
    duplicateErrorTempIds,
    updateOptionText,
    addOption,
    deleteOption,
    updateChoiceRules,
  } = useChoiceRulesWithDuplicates(initialRules);

  // Update question whenever internal choiceRulesWithIds changes
  useEffect(() => {
    if (!isChoice) {
      return;
    }

    const cleanedRules = stripTempIds(choiceRulesWithIds);

    setQuestion((prev) => ({
      ...prev,
      validationRules: {
        choiceRules: cleanedRules,
      },
    }));
  }, [choiceRulesWithIds, setQuestion, isChoice]);

  // Handle questionType changes (SingleChoice <-> MultipleChoice toggle)
  // Convert Required → Is when switching to SingleChoice
  useEffect(() => {
    if (!isChoice) {
      return;
    }

    if (question.questionType === QuestionType.SingleChoice) {
      const needsConversion = choiceRulesWithIds.some(
        (rule) => rule.qualifier === ChoiceQualifyingExpression.Required,
      );

      if (needsConversion) {
        const updatedRules = choiceRulesWithIds.map((rule) => ({
          ...rule,
          qualifier:
            rule.qualifier === ChoiceQualifyingExpression.Required
              ? ChoiceQualifyingExpression.Is
              : rule.qualifier,
        }));
        updateChoiceRules(updatedRules);
      }
    }
  }, [question.questionType, choiceRulesWithIds, updateChoiceRules, isChoice]);

  const onDragEnd = useGetDragEndEventHandler(updateChoiceRules);

  const addOptionDisabled = useMemo(
    () => choiceRulesWithIds.length >= MAX_CHOICES,
    [choiceRulesWithIds.length],
  );

  const handleAddOption = useCallback(() => {
    track("ADD OPTION + Button Clicked");
    addOption();
  }, [track, addOption]);

  const handleAddCondition = useCallback(() => {
    track("ADD CONDITION + Button Clicked");

    // Find next available non-duplicate option without a qualifier
    const targetOption =
      choiceRulesWithIds.find(
        (option) => !option.qualifier && !duplicateErrorTempIds.has(option.id),
      ) ??
      // If all options have qualifiers, use the first non-duplicate option
      choiceRulesWithIds.find(
        (option) => !duplicateErrorTempIds.has(option.id),
      );

    if (!targetOption) {
      return;
    }

    const defaultQualifier = ChoiceQualifyingExpression.Is;
    const updatedChoiceRules = choiceRulesWithIds.map((rule) =>
      rule.id === targetOption.id
        ? { ...rule, qualifier: defaultQualifier }
        : rule,
    );

    setQuestion((prev) => ({
      ...prev,
      isQualifying: true,
    }));
    updateChoiceRules(updatedChoiceRules);
  }, [
    choiceRulesWithIds,
    duplicateErrorTempIds,
    track,
    updateChoiceRules,
    setQuestion,
  ]);

  const handleDeleteCondition = useCallback(
    (option: ChoiceRuleWithId) => {
      const updatedChoiceRules = [...choiceRulesWithIds];
      const index = choiceRulesWithIds.findIndex((item) => item === option);

      if (updatedChoiceRules[index]) {
        updatedChoiceRules[index] = {
          ...updatedChoiceRules[index],
          qualifier: undefined,
        };
      }

      const hasQualifiers = updatedChoiceRules.some((rule) => !!rule.qualifier);

      setQuestion((prev) => ({
        ...prev,
        isQualifying: hasQualifiers,
      }));
      updateChoiceRules(updatedChoiceRules);
    },
    [choiceRulesWithIds, updateChoiceRules, setQuestion],
  );

  const handleConditionChange = useCallback(
    (
      type: "qualifier" | "optionText",
      tempId: string,
      value?: string | null,
    ) => {
      const currentOption = choiceRulesWithIds.find((opt) => opt.id === tempId);
      if (!currentOption) {
        return;
      }

      if (type === "qualifier") {
        return updateChoiceRules(
          choiceRulesWithIds.map((rule) =>
            rule.id === tempId
              ? {
                  ...rule,
                  qualifier: value as Maybe<ChoiceQualifyingExpression>,
                }
              : rule,
          ),
        );
      } else if (type === "optionText") {
        if (!value) {
          return;
        }

        // Find target option by text, preferring non-duplicate options
        const matchingOptions = choiceRulesWithIds.filter(
          (opt) => opt.optionText === value && opt.id !== tempId,
        );

        // Prefer non-duplicate options first, then fall back to duplicates
        const targetOption =
          matchingOptions.find((opt) => !duplicateErrorTempIds.has(opt.id)) ??
          matchingOptions[0];

        if (!targetOption) {
          return;
        }

        // Transfer qualifier from current to target option
        updateChoiceRules(
          choiceRulesWithIds.map((rule) => {
            if (rule.id === tempId) {
              return { ...rule, qualifier: undefined };
            }
            if (rule.id === targetOption.id) {
              return { ...rule, qualifier: currentOption.qualifier };
            }
            return rule;
          }),
        );
      }
    },
    [choiceRulesWithIds, duplicateErrorTempIds, updateChoiceRules],
  );

  const renderOptions = () => (
    <Box>
      <DragDropProvider onDragEnd={onDragEnd}>
        <Stack gap="sm">
          {choiceRulesWithIds.map((option, index) => (
            <ChoiceOption
              index={index}
              isDuplicate={duplicateErrorTempIds.has(option.id)}
              key={option.id}
              onDeleteClick={deleteOption}
              onOptionTextChange={updateOptionText}
              option={option}
            />
          ))}
        </Stack>
      </DragDropProvider>
      <Tooltip
        disabled={!addOptionDisabled}
        label={`Multiple choice questions can't have more than ${MAX_CHOICES} options`}
      >
        <Button
          className="disabled:bg-transparent disabled:opacity-50 disabled:text-uw-purple px-0 w-min"
          disabled={addOptionDisabled}
          mt="sm"
          onClick={handleAddOption}
          rightSection={<FontAwesomeIcon icon={faPlus} />}
          size="xs"
          variant="transparent"
        >
          ADD OPTION
        </Button>
      </Tooltip>
    </Box>
  );

  const renderValidationRules = () => {
    // Only show for SingleChoice, not MultipleChoice
    if (question.questionType !== QuestionType.SingleChoice) {
      return null;
    }

    // Filter options that have a non-null qualifier
    const conditions = choiceRulesWithIds.filter(
      (option) => !!option.qualifier,
    );

    // Map qualifiers for display
    const optionMap: Partial<Record<ChoiceQualifyingExpression, string>> = {
      [ChoiceQualifyingExpression.Is]: "is",
      [ChoiceQualifyingExpression.IsNot]: "is not",
    };

    const reverseOptionMap: Record<string, ChoiceQualifyingExpression> =
      Object.entries(optionMap).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [value]: key as ChoiceQualifyingExpression,
        }),
        {} as Record<string, ChoiceQualifyingExpression>,
      );

    return (
      <>
        {question.isQualifying &&
          conditions.length > 0 &&
          conditions.map((option, index) => (
            <Flex direction="column" key={`condition-${index}`} gap="sm">
              <Flex align="center" gap="xs">
                <Text size="sm" fw={400}>
                  Answer
                </Text>
                <Select
                  allowDeselect={false}
                  className="min-w-[120px]"
                  data={Object.entries(optionMap).map(([, displayLabel]) => ({
                    value: displayLabel,
                    label: displayLabel,
                  }))}
                  onChange={(displayValue) => {
                    const enumKey = displayValue
                      ? reverseOptionMap[displayValue]
                      : null;

                    const matchingOption = choiceRulesWithIds.find(
                      (item) =>
                        item.optionText === option.optionText &&
                        item.qualifier === option.qualifier,
                    );

                    if (matchingOption) {
                      handleConditionChange(
                        "qualifier",
                        matchingOption.id,
                        enumKey,
                      );
                    }
                  }}
                  value={option.qualifier ? optionMap[option.qualifier] : null}
                />
                <Select
                  className="flex-grow"
                  data={choiceRulesWithIds
                    .filter((opt) => !duplicateErrorTempIds.has(opt.id))
                    .map((opt) => opt.optionText)}
                  onChange={(value) => {
                    const matchingOption = choiceRulesWithIds.find(
                      (item) =>
                        item.optionText === option.optionText &&
                        item.qualifier === option.qualifier,
                    );

                    if (matchingOption) {
                      handleConditionChange(
                        "optionText",
                        matchingOption.id,
                        value || "",
                      );
                    }
                  }}
                  placeholder="Select option"
                  value={option.optionText}
                />
                <ActionIcon
                  onClick={() => handleDeleteCondition(option)}
                  variant="transparent"
                >
                  <FontAwesomeIcon icon={faXmark} size="sm" />
                </ActionIcon>
              </Flex>
            </Flex>
          ))}
        <AddConditionButton onClick={handleAddCondition} />
      </>
    );
  };

  return isChoice
    ? {
        renderOptions,
        renderValidationRules,
      }
    : {
        renderOptions: () => null,
        renderValidationRules: () => null,
      };
};
